import React, {ReactElement} from 'react';
import {ServiceBase} from '../ServiceBase';
import {
    IGermanyResponsibleGamingButtonOptions,
    IOrbitComponentInSdk,
    IOrbitDialogInSdk
} from '../../../common/interfaces/orbit-component-to-sdk-adapter/orbit-component-to-sdk-adapter.service.interface';
import {
    ITimeSpentReminderWithBalanceComponentProps,
    ITimeSpentReminderOptions
} from '../../../common/interfaces/orbit-component-to-sdk-adapter/components-props/time-spent-reminder-options';
import {
    ResponsibleGamingButtonsGermanyComponent
} from "../../components/responsible-gambling/responsible-gaming-buttons/regulations/germany/responsible-gaming-buttons-germany.component";
import {action, observable, makeObservable} from 'mobx';
import {IOrbitComponentsInsideSdkProvider, IComponentRenderedInsideSdk} from './orbit-components-inside-sdk-provider.interface';
import {TimeSpentReminderWithDropdownComponent} from "../../components/time-spent-reminder/with-dropdown/time-spent-reminder-with-dropdown.component";
import {
    DialogButtonClickHandleResultEnum,
    ShowModalOptions
} from "../dialog/dialog.service.interface";
import {GeoPermissionsPopupComponent} from "../../components/geo/geo-permission-popup/geo-permission-popup.component";
import {IStatePopupDisplayParams} from "../../../common/interfaces/orbit-component-to-sdk-adapter/components-props/state-popup-display-params";
import {IGeoPermissionPopupOptions} from "../../../common/interfaces/orbit-component-to-sdk-adapter/components-props/geo-permission-popup.component-props";
import {TimeSpentReminderWithBalanceComponent} from "../../components/time-spent-reminder/with-balance/time-spent-reminder-with-balance.component";
import {IdleRecommendationsComponentProps} from '../../../common/interfaces/orbit-component-to-sdk-adapter/components-props/idle-recommendations.component-props';
import {IdleRecommendationComponent} from '../../components/idle-recommendation/idle-recommendation.component';
import {
    PRIORITY_LEVEL,
    PRIORITY_LEVEL_ZINDEX_MAPPER
} from "../../../sdk/src/services/overlay_manager/overlay_manager_levels";
import {
    IIntrusiveMessagePopupDisplayParams
} from "../../../common/interfaces/orbit-component-to-sdk-adapter/components-props/intrusive-message-popup-display-params";
import {DISPLAY_MESSAGE_IDS, DISPLAY_MESSAGE_NAMES} from "../../../common/const/casinofied-analytics-consts";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {TempAccountPopupComponent} from "../../components/temp-account-popup/temp-account-popup.component";
import {
    ITempAccountShowPopupOptions
} from "../../../common/interfaces/orbit-component-to-sdk-adapter/components-props/temp-account-popup-params";
import {InitBiometricDialogComponent} from '../../components/biometric-dialog/init-biometric-dialog/init-biometric-dialog.component';
import {LoginToastComponent} from '../../components/login-toast/login-toast.component';
import {ILoginToastDef} from '../../../common/interfaces/orbit-component-to-sdk-adapter/components-props/login-toast.component-props';

export class OrbitComponentToSdkAdapterService extends ServiceBase implements IOrbitComponentsInsideSdkProvider {
    constructor(services: IServiceFactoryExtended) {
        super(services);

        makeObservable(this);
    }

    renderGermanyResponsibleGamingButtons(element: HTMLElement, options?: IGermanyResponsibleGamingButtonOptions): IOrbitComponentInSdk {
        return this._renderComponent(element,
            <ResponsibleGamingButtonsGermanyComponent key={'GermanyResponsibleGamingButtons'} placement={options?.placement} />);
    }

    showLocationPopup(props: IStatePopupDisplayParams): Promise<void> {
        return this.services.stateOfResidenceIdentifier.displayStateSelectorPopup(props);
    }

    showGeoPermissionPopup(props: IGeoPermissionPopupOptions) {
        return this.services.dialog.showModal(dialogHandler =>
            <GeoPermissionsPopupComponent
                dialogHandler={dialogHandler}
                onOkPressed={props.onOkPressed}
                headerText1={this.services.translation.translate("GEO_PERMISSIONS_POPUP_TOP_TEXT")}
                headerText2={this.services.translation.translate("GEO_PERMISSIONS_POPUP_MIDDLE_TEXT")}

            />, {
            title: this.services.translation.translate("GEO_PERMISSIONS_POPUP_TITLE"),
            icon: this.services.theme.currentTheme.icons.locationIcon,
            forceTransparentBackground: true,
            disableFullscreenMobile: true,
            forceCentralization: true,
            mobileCenterHeader: true,
            isHighPriority: true,
            forceZOrder: PRIORITY_LEVEL_ZINDEX_MAPPER[PRIORITY_LEVEL.TOUCH_ID] - 1,
            onClosed: props.onMessageClosed,
            shouldBeClosedOnlyByUser: true,
            hideCloseButton: true,
        }
        );
    }

    private _getTimeSpentReminderDialogOptions(headerClass?: string): ShowModalOptions {
        return {
            title: this.services.translation.translate("TSR_TITLE"),
            forceTransparentBackground: true,
            disableFullscreenMobile: true,
            forceCentralization: true,
            mobileCenterHeader: true,
            isHighPriority: true,
            onClosed: null,
            shouldBeClosedOnlyByUser: true,
            hideCloseButton: true,
            headerClass: headerClass,
            showPopupAboveSecondWebViewHybrid: true,
            icon: this.services.theme.currentTheme.icons.timeSpentReminderClockIcon
        };
    }

    showTempAccountPopup(options: ITempAccountShowPopupOptions): IOrbitDialogInSdk {
        return this.services.dialog.showModal(dialogHandler => <TempAccountPopupComponent
            bodyText={options.bodyText}
            daysLeft={options.daysLeft}
            status={options.status}
            footerText={options.footerText}
            dialogHandler={dialogHandler}
        />, {
            onClosedByXButton: () => this.services.analytics.sendAnalytics('temp_account_message', 'close'),
            forceCentralization: true,
            mobileCenterHeader: true,
            isHighPriority: true,
            shouldBeClosedOnlyByUser: true,
            title: this.services.translation.translate('temp_account_title'),
            icon: this.services.theme.currentTheme.icons.tempAccountPopupIcon
        });
    }

    showTimeSpentReminderWithDropdown(options: ITimeSpentReminderOptions): IOrbitDialogInSdk {

        const dialogOptions = this._getTimeSpentReminderDialogOptions('cy-uk-malta-reality-check-title');

        return this.services.dialog.showModal(dialogHandler => <TimeSpentReminderWithDropdownComponent
            {...options}
            dialogHandler={dialogHandler} />
            , dialogOptions);
    }

    showTimeSpentReminderWithBalance(options: ITimeSpentReminderWithBalanceComponentProps): IOrbitDialogInSdk {

        const dialogOptions = this._getTimeSpentReminderDialogOptions();

        return this.services.dialog.showModal(dialogHandler => <TimeSpentReminderWithBalanceComponent
            {...options}
            dialogHandler={dialogHandler} />
            , dialogOptions);
    }


    showIntrusiveMessagePopup(params: IIntrusiveMessagePopupDisplayParams): IOrbitDialogInSdk {
        if (this.services.miniOrbit.isCip) {
            return; //We don't show the popup on our side for mini CIP, but we show it for CIS
        }

        const businessCorrelationId = this.services.tools.getGuid();

        this.services.casinofiedAnalytics.messageDisplay({
            businessCorrelationID: businessCorrelationId,
            displayMessageID: DISPLAY_MESSAGE_IDS.nightLimitWarning,
            displayMessageName: DISPLAY_MESSAGE_NAMES.DISRUPTIVE_WARNING_MESSAGE_NAME,
            correlationID: params.correlationId
        });

        const WarningIcon = this.services.theme.currentTheme.icons.warningSignIcon;
        return this.services.dialog.showSimpleTextModal({
            buttons: [
                {
                    buttonPreset: "primary",
                    text: params.buttonText,
                    onClick: async () => {
                        this.services.casinofiedAnalytics.messageActionTaken({
                            displayMessageName: DISPLAY_MESSAGE_NAMES.DISRUPTIVE_WARNING_MESSAGE_NAME,
                            actionType: 'Confirmation',
                            correlationID: params.correlationId,
                            businessCorrelationID: businessCorrelationId
                        });
                        return DialogButtonClickHandleResultEnum.CloseDialogAndContinue;
                    }
                }
            ],
            hideCloseButton: true,
            text: params.text,
            title: params.title.toLowerCase(),
            icon: <WarningIcon style={{display: "inline"}} size={5.6} />,
            disableFullscreenMobile: true,
            showPopupAboveSecondWebViewHybrid: true
        })
    }

    showDefaultKickPopup(popupTextKey: string): IOrbitDialogInSdk {
        const WarningIcon = this.services.theme.currentTheme.icons.warningSignIcon;
        return this.services.dialog.showSimpleTextModal({
            buttons: [
                {
                    buttonPreset: "primary",
                    text: this.services.translation.translate('OK'),
                    onClick: async () => {
                        return DialogButtonClickHandleResultEnum.CloseDialogAndContinue;
                    }
                }
            ],
            text: this.services.translation.translate(popupTextKey),
            title: this.services.translation.translate('generic_notification_popup_title').toLowerCase(),
            icon: <WarningIcon style={{display: "inline"}} size={5.6} />,
            disableFullscreenMobile: true,
            showPopupAboveSecondWebViewHybrid: true
        });
    }

    showDisconnectPopup(): IOrbitDialogInSdk {
        if (this.services.miniOrbit.isEnabled) {
            return; //We don't show the disconnected popup on our side for miniOrbits
        }

        const WarningIcon = this.services.theme.currentTheme.icons.warningSignIcon;
        return this.services.dialog.showSimpleTextModal({
            buttons: [
                {
                    buttonPreset: "primary",
                    text: this.services.translation.translate('OK'),
                    onClick: async () => {
                        this.services.analytics.sendOrbitUserFlowEvent('InactivityTimeoutPopUp', 'OK', 'PostDisconnect');
                        return DialogButtonClickHandleResultEnum.CloseDialogAndContinue;
                    }
                }
            ],
            text: this.services.translation.translate('MDP_INACTIVITY_DISCONNECTED_TEXT'),
            title: this.services.translation.translate('generic_notification_popup_title').toLowerCase(),
            icon: <WarningIcon style={{display: "inline"}} size={5.6} />,
            disableFullscreenMobile: true,
            showPopupAboveSecondWebViewHybrid: true,
            onClosedByXButton: async () => {
                this.services.analytics.sendOrbitUserFlowEvent('InactivityTimeoutPopUp', 'Close', 'PostDisconnect');
            }
        });
    }

    renderIdleRecommendations(element: HTMLElement, props: IdleRecommendationsComponentProps): IOrbitComponentInSdk {
        return this._renderComponent(element,
            <IdleRecommendationComponent key={'IdleRecommendation'} {...props} />
        );
    }

    @observable.ref
    currentRenderedComponents: IComponentRenderedInsideSdk[] = [];

    @action.bound
    private _setCurrentRenderedComponents(currentRenderedComponents: IComponentRenderedInsideSdk[]) {
        this.currentRenderedComponents = currentRenderedComponents;
    }

    private _unmountComponent(element: HTMLElement) {
        const index = this.currentRenderedComponents.findIndex(item => item.element === element);
        if (index >= 0) {
            this._setCurrentRenderedComponents([...this.currentRenderedComponents.splice(index, 1)])
        }
    }

    private _renderComponent(element: HTMLElement, component: ReactElement): IOrbitComponentInSdk {

        this._setCurrentRenderedComponents([
            ...this.currentRenderedComponents,
            {
                component: component,
                element: element
            }
        ]);

        return {
            unmount: () => {
                this._unmountComponent(element)
            }
        }
    }

    showAboutDialog(): Promise<void> {
        return this.services.regulation.current.components.showAboutDialog()
    }

    showInitBiometricDialog(): IOrbitDialogInSdk {
        return this.services.dialog.showModal(dialogHandler => <InitBiometricDialogComponent
            dialogHandler={dialogHandler}
        />, {
            title: this.services.translation.translate('TOUCH_ID_AUTO_SCREEN'),
            forceTransparentBackground: true,
            disableFullscreenMobile: true,
            forceCentralization: true,
            shouldBeClosedOnlyByUser: true,
            mobileCenterHeader: true,
            onClosed: () => this.services.sdk.eventsTrigger.onInitScreenShow(),
        });
    }

    showLoginMessageToast(toastConfig: ILoginToastDef): void {
        this.services.toaster.showCustomToast({
            id: toastConfig.id || "login_message_toast",
            toastOptions: toastConfig.toastOptions,
            onToastClosed: toastConfig.onToastClosed,
            renderContent: (handler) => {return <LoginToastComponent contentProps={toastConfig.contentProps} />}
        })
    }

}